/**
 * Reducer.ts
 * @author Diao Zheng
 * @file A dispatcher for use in React Components
 */
var _a;
import { __awaiter } from "tslib";
import _ from "lodash";
import { assert, Disposable, invariant, LinkedList, Option } from "nasi";
import { Dispatcher } from "./Dispatcher";
import * as Stable from "./Stable";
export class ReducableDispatcher extends Dispatcher {
    constructor(reducer, initialState, dispatchToParent, debugName) {
        super(debugName);
        this.dispatch = Stable.declareAsStable((action) => {
            this.reducer(this.currentState, action);
            super.dispatch(action);
        });
        this.sideEffectsQueue = new LinkedList();
        this.asynchronousEffectCounter = 0;
        this.attachDispatcher = (dispatchToParent) => {
            if (this.dispatchToParent !== dispatchToParent) {
                this.boundReducerFunction = this.reducerFunction(dispatchToParent);
            }
        };
        this.reducer = (state, action) => {
            const intent = this.boundReducerFunction(state, action);
            if (!intent) {
                assert(Option.isSome, intent);
                return {};
            }
            this.executeOrQueueEffects(state, intent);
            if (intent.update) {
                return intent.update;
            }
            return {};
        };
        this[_a] = () => {
            invariant(() => this.sideEffectsQueue.length === 0, "There are still pending effects in queue during willUnmount");
            invariant(() => this.asynchronousEffectCounter === 0, `There are uncaught asynchronous calls in ${this.constructor.name}` +
                "during willUnmount.", false);
            super[Disposable.Dispose]();
        };
        this.executeEffect = (effect) => __awaiter(this, void 0, void 0, function* () {
            const response = effect();
            if (response) {
                ++this.asynchronousEffectCounter;
                yield response;
                --this.asynchronousEffectCounter;
            }
        });
        this.reducerFunction = reducer;
        this.dispatchToParent = (dispatchToParent !== null && dispatchToParent !== void 0 ? dispatchToParent : _.identity);
        this.boundReducerFunction = this.reducerFunction((dispatchToParent !== null && dispatchToParent !== void 0 ? dispatchToParent : _.identity));
        this.currentState = initialState;
    }
    INTERNAL_reconcileAfterComponentUpdate(nextState) {
        this.sideEffectsQueue.drain(this.executeEffect);
        this.currentState = nextState;
    }
    UNSAFE_dispatchToParentDirectly(action) {
        this.dispatchToParent(action);
    }
    executeOrQueueEffects(state, intent) {
        if (Option.isNone(intent.effect)) {
            return;
        }
        if (Option.isSome(intent.update)) {
            for (const effect of intent.effect) {
                this.sideEffectsQueue.push(effect.bind(undefined, state));
            }
        }
        else {
            for (const effect of intent.effect) {
                effect(state);
            }
        }
    }
}
_a = Disposable.Dispose;
